import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MemeDashboardService } from '@src/app/modules/meme-dashboard/meme-dashboard.service';
import { MemeToken, TokenMarketCapType } from '@src/app/modules/meme-dashboard/meme-dashboard.type';
import { getTokenMarketCapType } from '@src/app/modules/meme-dashboard/meme-dashboard.util';
import { CommonService } from '@src/app/services/common.service';
import { ChainEnum } from '@src/app/shared/types/wallet-chain.type';

interface PercentageMap {
    small: number;
    medium: number;
    large: number;
}

@Component({
    selector: 'app-token-treemap-by-chain',
    templateUrl: './token-treemap-by-chain.component.html',
    styleUrls: ['./token-treemap-by-chain.component.less'],
})
export class TokenTreemapByChainComponent implements OnChanges {
    ChainEnum = ChainEnum;

    @Input() chain: ChainEnum.BSC | ChainEnum.BASE | 'sol' | 'monad_testnet';
    @Input() valueType: 'smart_buy' | 'watchlist';
    @Input() dateRange: string;
    @Input() selectedSubscribedWatchlist: number;
    @Input() smartMoneyChecked = true;
    @Input() myWatchListChecked = true;

    smartMoneyList: MemeToken[] = [];
    myWatchList: MemeToken[] = [];
    subscribedWatchlistListTokens: MemeToken[] = [];

    shownDetailedTreemap: TokenMarketCapType | null = null;
    treemapList: { type: TokenMarketCapType; data: MemeToken[] }[] = [];
    percentageMap: PercentageMap = {
        large: 0.33,
        medium: 0.33,
        small: 0.33,
    };
    loading = false;

    constructor(private memeDashboardService: MemeDashboardService, private commonService: CommonService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['dateRange']?.currentValue || changes['selectedSubscribedWatchlist']?.currentValue) {
            this.fetchData();
        } else if (changes['smartMoneyChecked'] || changes['myWatchListChecked']) {
            this.treemapDataSourceChange();
        }
    }

    fetchData() {
        this.loading = true;
        const requestList = [
            this.memeDashboardService.getMemeTokenTreemapData(this.dateRange, this.chain),
            this.memeDashboardService.getMyWatchListTreemapData(this.dateRange, this.chain),
        ];

        if (this.selectedSubscribedWatchlist) {
            requestList.push(this.memeDashboardService.getWatchlistTreemapData(this.selectedSubscribedWatchlist, this.dateRange, this.chain));
        }

        Promise.allSettled(requestList)
            .then(([smartMoneyRes, myWatchRes, subscribedWatchlistRes]) => {
                if (smartMoneyRes?.status === 'fulfilled') {
                    this.smartMoneyList = smartMoneyRes.value.data;
                }

                if (myWatchRes?.status === 'fulfilled') {
                    this.myWatchList = myWatchRes.value.data;
                }

                if (subscribedWatchlistRes?.status === 'fulfilled') {
                    this.subscribedWatchlistListTokens = subscribedWatchlistRes.value.data;
                }

                this.treemapDataSourceChange();
            })
            .catch(err => this.commonService.errorMessageByResponse(err))
            .finally(() => (this.loading = false));
    }

    treemapDataSourceChange() {
        this.storeTreemapDataSourceStatus();
        const mergedMap = new Map<string, MemeToken>();

        if (this.smartMoneyChecked) {
            this.smartMoneyList.forEach(token => {
                const key = `${token.chain_name}-${token.token_address}`;
                token.list_type = 'smart_buy';
                mergedMap.set(key, token);
            });
        }

        if (this.selectedSubscribedWatchlist) {
            this.subscribedWatchlistListTokens.forEach(token => {
                const key = `${token.chain_name}-${token.token_address}`;
                token.list_type = 'smart_buy';
                mergedMap.set(key, token);
            });
        }

        if (this.myWatchListChecked) {
            this.myWatchList.forEach(token => {
                const key = `${token.chain_name}-${token.token_address}`;
                if (mergedMap.has(key)) {
                    // 如果已存在，说明在两个列表中都出现了
                    mergedMap.get(key).list_type = 'both';
                } else {
                    // 如果不存在，说明只在 watchlist 中出现
                    token.list_type = 'my_watchlist';
                    mergedMap.set(key, token);
                }
            });
        }

        // 只要smartMoneyChecked为true，则valueType为smart_buy
        if (this.smartMoneyChecked) {
            this.valueType = 'smart_buy';
        } else {
            this.valueType = 'watchlist';
        }

        this.generateTreemapList(Array.from(mergedMap.values()));
    }

    shownDetailedTreemapChange(type: TokenMarketCapType) {
        if (this.shownDetailedTreemap === type) {
            this.shownDetailedTreemap = null;
        } else {
            this.shownDetailedTreemap = type;
        }
    }

    private generateTreemapList(data: MemeToken[]) {
        const largeData: MemeToken[] = [];
        const midData: MemeToken[] = [];
        const smallData: MemeToken[] = [];

        data.forEach(item => {
            const type = getTokenMarketCapType(item.market_cap);
            switch (type) {
                case 'large':
                    largeData.push(item);
                    break;
                case 'medium':
                    midData.push(item);
                    break;
                case 'small':
                    smallData.push(item);
                    break;
            }
        });

        this.treemapList = [
            ...(largeData.length ? [{ type: 'large', data: largeData }] : []),
            ...(midData.length ? [{ type: 'medium', data: midData }] : []),
            ...(smallData.length ? [{ type: 'small', data: smallData }] : []),
        ] as any;

        this.generatePercentageMap();
    }

    generatePercentageMap() {
        const valueKey = this.valueType === 'smart_buy' ? 'smart_money_swap_amount_usd' : 'watchlist_buy_amount_usd';

        const r1 = this.treemapList.find(item => item.type === 'large')?.data.reduce((acc, cur) => acc + cur[valueKey], 0) || 0;
        const r2 = this.treemapList.find(item => item.type === 'medium')?.data.reduce((acc, cur) => acc + cur[valueKey], 0) || 0;
        const r3 = this.treemapList.find(item => item.type === 'small')?.data.reduce((acc, cur) => acc + cur[valueKey], 0) || 0;
        const sum = r1 + r2 + r3;

        this.percentageMap = this.adjustPercentages({
            large: r1 / sum,
            medium: r2 / sum,
            small: r3 / sum,
        });
    }

    private adjustPercentages(original: PercentageMap): PercentageMap {
        const result = { ...original };
        const BASE_RATIOS = {
            large: 0.3, // 3
            medium: 0.4, // 4
            small: 0.3, // 3
        };

        // 找出哪些类型有数据（值大于0）
        const activeTypes = Object.entries(result)
            .filter(([_, value]) => value > 0)
            .map(([key]) => key as keyof PercentageMap);

        if (activeTypes.length === 0) return result;

        // 计算活跃类型的基础比例总和
        const totalBaseRatio = activeTypes.reduce((sum, type) => sum + BASE_RATIOS[type], 0);

        // 按比例分配
        activeTypes.forEach(type => {
            result[type] = BASE_RATIOS[type] / totalBaseRatio;
        });

        // 将没有数据的类型设置为0
        Object.keys(result).forEach(key => {
            if (!activeTypes.includes(key as keyof PercentageMap)) {
                result[key as keyof PercentageMap] = 0;
            }
        });

        return result;
    }

    private storeTreemapDataSourceStatus() {
        if (this.smartMoneyChecked) {
            localStorage.removeItem('ss_meme_smart_money_hidden');
        } else {
            localStorage.setItem('ss_meme_smart_money_hidden', '1');
        }

        if (this.myWatchListChecked) {
            localStorage.removeItem('ss_meme_my_watchlist_hidden');
        } else {
            localStorage.setItem('ss_meme_my_watchlist_hidden', '1');
        }

        if (this.selectedSubscribedWatchlist) {
            localStorage.setItem('ss_meme_subscribed_watchlist', this.selectedSubscribedWatchlist.toString());
        } else {
            localStorage.removeItem('ss_meme_subscribed_watchlist');
        }
    }
}
